/**
 * Mantras: a short phrase repeated as a focusing practice.
 *
 * Built in as CODE with keyed overrides, exactly like the breathwork patterns.
 * The prototype served these from an admin content library, which meant the
 * tab was empty until somebody filled it; shipping a set means the feature
 * works on the day it is switched on, and a tenant can still replace or hide
 * any of it with a `coaching_content` row of kind `mantra`.
 *
 * Every line is written as tradition and reflection, never as cause. A mantra
 * is repeated FOR something the way a prayer is, not the way a treatment is:
 * the copy says "traditionally repeated", and `COACHING_MANTRA_DISCLAIMER`
 * goes on every screen that shows one.
 */

export const COACHING_MANTRA_INTENTIONS = [
  'peace',
  'clarity',
  'abundance',
  'courage',
  'beginnings',
  'goodwill',
] as const;
export type CoachingMantraIntention = (typeof COACHING_MANTRA_INTENTIONS)[number];

export const COACHING_MANTRA_INTENTION_LABELS: Record<CoachingMantraIntention, string> = {
  peace: 'Peace',
  clarity: 'Clarity',
  abundance: 'Abundance',
  courage: 'Courage',
  beginnings: 'Beginnings',
  goodwill: 'Goodwill',
};

export const COACHING_MANTRA_DISCLAIMER =
  'Mantras are offered as a traditional focusing practice for reflection. They are not a treatment and they do not guarantee an outcome.';

/** A quarter mala, a half, and the full round. The screen counts to one of these. */
export const COACHING_MANTRA_COUNTS = [27, 54, 108] as const;
export const COACHING_MANTRA_DEFAULT_COUNT = 27;

export const BUILT_IN_MANTRA_KEYS = [
  'om',
  'om-shanti',
  'so-ham',
  'gayatri',
  'ganesha',
  'lakshmi',
  'om-namah-shivaya',
  'lokah-samastah',
] as const;
export type BuiltInMantraKey = (typeof BUILT_IN_MANTRA_KEYS)[number];

export type BuiltInMantra = {
  key: BuiltInMantraKey;
  /** The transliteration, which is what the member reads and repeats. */
  title: string;
  /** Devanagari, shown beside the transliteration. Optional for a tenant's own. */
  script: string;
  /** What the words say, plainly. */
  meaning: string;
  /** When practitioners traditionally turn to it. */
  use: string;
  intention: CoachingMantraIntention;
  /** Suggested repetitions; 108 is the traditional full round. */
  repetitions: number;
};

export const BUILT_IN_MANTRAS: readonly BuiltInMantra[] = [
  {
    key: 'om',
    title: 'Om',
    script: 'ॐ',
    meaning:
      'The single syllable said to underlie all sound. It carries no request: it is a place to put your attention.',
    use: 'A first mantra, and the one to come back to. Good when the mind is too busy for words with meaning in them.',
    intention: 'peace',
    repetitions: 27,
  },
  {
    key: 'om-shanti',
    title: 'Om Shanti Shanti Shanti',
    script: 'ॐ शान्तिः शान्तिः शान्तिः',
    meaning:
      'Peace, said three times: traditionally for peace in the body, in the mind, and in what surrounds you.',
    use: 'At the end of a practice, or after a day that ended badly and you would rather not carry into sleep.',
    intention: 'peace',
    repetitions: 27,
  },
  {
    key: 'so-ham',
    title: 'So Ham',
    script: 'सो ऽहम्',
    meaning: '"That I am." It is paired with the breath: so on the way in, ham on the way out.',
    use: 'When you want the practice to run on the breath rather than on counting. It settles quickly.',
    intention: 'peace',
    repetitions: 54,
  },
  {
    key: 'gayatri',
    title: 'Om Bhur Bhuvah Svaha, Tat Savitur Varenyam',
    script: 'ॐ भूर्भुवः स्वः तत्सवितुर्वरेण्यम्',
    meaning:
      'The opening of the Gayatri: a turning toward the light of the sun, asking that it illuminate the understanding.',
    use: 'Traditionally at sunrise. Practitioners turn to it before work that needs a clear head.',
    intention: 'clarity',
    repetitions: 27,
  },
  {
    key: 'ganesha',
    title: 'Om Gam Ganapataye Namaha',
    script: 'ॐ गं गणपतये नमः',
    meaning: 'A salutation to Ganesha, who in tradition is turned to at the start of things.',
    use: 'Before something new: a first meeting, a launch, a difficult conversation you have been putting off.',
    intention: 'beginnings',
    repetitions: 27,
  },
  {
    key: 'lakshmi',
    title: 'Om Shreem Mahalakshmiyei Namaha',
    script: 'ॐ श्रीं महालक्ष्म्यै नमः',
    meaning:
      'A salutation to Lakshmi, associated in tradition with abundance in its widest sense, not money alone.',
    use: 'Traditionally on a Friday, and when you want to practise receiving rather than chasing.',
    intention: 'abundance',
    repetitions: 108,
  },
  {
    key: 'om-namah-shivaya',
    title: 'Om Namah Shivaya',
    script: 'ॐ नमः शिवाय',
    meaning:
      'A salutation to Shiva, who in tradition is associated with what has to end before something else can begin.',
    use: 'When something is finishing and you are holding on to it. It is a mantra for letting go.',
    intention: 'courage',
    repetitions: 108,
  },
  {
    key: 'lokah-samastah',
    title: 'Lokah Samastah Sukhino Bhavantu',
    script: 'लोकाः समस्ताः सुखिनो भवन्तु',
    meaning:
      '"May all beings everywhere be happy and free." It asks nothing for the one saying it.',
    use: 'To close a practice, or on a day you have been keeping score against somebody.',
    intention: 'goodwill',
    repetitions: 27,
  },
];

export function isBuiltInMantraKey(key: string | null): key is BuiltInMantraKey {
  return key !== null && (BUILT_IN_MANTRA_KEYS as readonly string[]).includes(key);
}
